import { TranslateService } from '@ngx-translate/core';

/**
 * Display name of a default (system-seeded) folder.
 *
 * FeatureFolder rows store their name in English - "OHS-plans", "Other
 * documents" - and carry a `TranslateKey` that indexes into the
 * `DEFAULT_FOLDER` block of the i18n files. The document tree runs every node
 * through the `defaultFolderTranslate` pipe, so it shows "BHT-planer"; any
 * view that binds `filename` straight from the API instead leaks the English
 * name (Flexi > Documents showed both spellings side by side, translated in
 * the tree and untranslated in the file list).
 *
 * The eligibility rule is the pipe's: only folders the system seeded
 * (`isDefault`) are renamed, so a folder a business renamed itself keeps its
 * own name. `IMAGES` is the one exception - project image folders are created
 * per project and are not flagged default, but their key is still meant to be
 * translated.
 *
 * @param node      a tree node / file-list row (TreeViewDisplayDto shape)
 * @param translate the app's TranslateService
 * @returns the translated name, or the node's own `filename` when the node is
 *          not a default folder or its key has no translation
 */
export function defaultFolderDisplayName(
  node: any,
  translate: TranslateService
): string {
  if (!node) {
    return '';
  }
  if (!hasTranslatableFolderName(node)) {
    return node.filename;
  }
  const translateKey = node.translateKey;
  const defaultFolders = translate.instant('DEFAULT_FOLDER');
  const fromDefaultFolders =
    defaultFolders && typeof defaultFolders === 'object'
      ? defaultFolders[translateKey]
      : null;
  if (fromDefaultFolders) {
    return fromDefaultFolders;
  }
  // Some keys (IMAGES among them) live at the top level rather than under
  // DEFAULT_FOLDER; instant() echoes the key back when it finds nothing.
  const direct = translate.instant(translateKey);
  return direct && direct !== translateKey ? direct : node.filename;
}

/**
 * Whether `defaultFolderDisplayName` would rename this node - a system-seeded
 * folder carrying a translate key. Lets callers skip the lookup (and, in the
 * tree pipe's case, skip subscribing to language changes) for the nodes it
 * would leave untouched.
 */
export function hasTranslatableFolderName(node: any): boolean {
  if (!node || !node.translateKey) {
    return false;
  }
  return (
    node.isManualArticle !== true &&
    (node.isDefault === true || node.translateKey === 'IMAGES')
  );
}
